<template>
    <div>
        <!-- Header -->
        <div class="mb-12 text-center">
            <h1 class="blog-title text-gray-900 dark:text-white mb-4">Blog GROWNi</h1>
            <p class="text-xl text-gray-600 dark:text-gray-300 mb-6">
                Príbehy, tipy a inšpirácia pre váš osobný a profesionálny rast
            </p>

            <!-- Author Actions -->
            <div v-if="isAuthenticated" class="flex justify-center gap-4">
                <NuxtLink
                    to="/my-blog/write"
                    class="inline-flex items-center gap-2 px-6 py-3 bg-primary-600 text-white rounded-full font-medium hover:bg-primary-700 transition-colors"
                >
                    <Icon name="heroicons:pencil-square" class="size-5" />
                    Napísať článok
                </NuxtLink>
                <NuxtLink
                    to="/my-blog"
                    class="inline-flex items-center gap-2 px-6 py-3 border-2 border-gray-300 dark:border-default text-gray-700 dark:text-gray-300 rounded-full font-medium hover:border-gray-400 transition-colors dark:hover:border-default"
                >
                    <Icon name="heroicons:document-text" class="size-5" />
                    Moje články
                </NuxtLink>
            </div>
        </div>

        <form class="mx-auto mb-8 flex max-w-2xl gap-2" role="search" @submit.prevent="commitSearch">
            <label class="sr-only" for="blog-search">Hľadať v článkoch</label>
            <input
                id="blog-search"
                v-model="searchQuery"
                type="search"
                maxlength="200"
                placeholder="Hľadať v článkoch..."
                class="min-w-0 flex-1 rounded-full border border-gray-300 bg-white px-5 py-3 text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600 dark:border-default dark:bg-surface-card dark:text-txt-headings"
            />
            <button
                type="submit"
                class="rounded-full bg-primary-600 px-5 py-3 font-medium text-white hover:bg-primary-700 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
            >
                Hľadať
            </button>
        </form>

        <nav aria-label="Témy článkov" class="mb-6 flex flex-wrap justify-center gap-2">
            <button
                type="button"
                :aria-pressed="!selectedTopic"
                class="rounded-full px-4 py-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                :class="
                    !selectedTopic
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-surface-subtle dark:text-txt-body dark:hover:bg-surface-subtle'
                "
                @click="setFilter('topic', '')"
            >
                Všetky témy
            </button>
            <button
                v-for="topic in BLOG_TOPICS"
                :key="topic.id"
                type="button"
                :aria-pressed="selectedTopic === topic.id"
                class="rounded-full px-4 py-2 text-sm font-medium focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600"
                :class="
                    selectedTopic === topic.id
                        ? 'bg-primary-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200 dark:bg-surface-subtle dark:text-txt-body dark:hover:bg-surface-subtle'
                "
                @click="setFilter('topic', topic.id)"
            >
                {{ topic.label }}
            </button>
        </nav>
        <div class="mb-8 flex flex-wrap items-center justify-center gap-3">
            <label for="blog-tag" class="text-sm text-gray-700 dark:text-txt-body">Tag</label>
            <select
                id="blog-tag"
                :value="selectedTag"
                class="max-w-full rounded-lg border border-gray-300 bg-white px-3 py-2 text-gray-900 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 dark:border-default dark:bg-surface-card dark:text-txt-headings"
                @change="setFilter('tag', ($event.target as HTMLSelectElement).value)"
            >
                <option value="">Všetky tagy</option>
                <option v-for="tag in allTags" :key="tag" :value="tag">{{ tag }}</option>
            </select>
            <button
                v-if="hasFilters"
                type="button"
                class="rounded px-3 py-2 text-sm text-primary-600 underline focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 dark:text-primary-300"
                @click="clearFilters"
            >
                Zrušiť filtre
            </button>
        </div>

        <div aria-live="polite" role="status" class="mb-6 text-center text-sm text-gray-600 dark:text-txt-body">
            {{ status === 'pending' ? 'Načítavam články...' : error ? '' : `${results?.total || 0} nájdených článkov` }}
        </div>
        <div v-if="error" class="py-12 text-center" role="alert">
            <p class="text-red-600 dark:text-red-300">Články sa nepodarilo načítať. Skúste to znova.</p>
            <button type="button" class="mt-4 rounded-lg border px-4 py-2" @click="refresh()">Skúsiť znova</button>
        </div>
        <div v-else-if="status === 'pending'" class="flex justify-center py-20" aria-busy="true">
            <CSpinner size="large" />
        </div>
        <template v-else>
            <BlogLatestSection
                v-if="results?.posts.length"
                :posts="results.posts"
                :title="sectionTitle"
                :show-view-all="false"
            />
            <div v-else class="py-12 text-center">
                <p class="text-gray-600 dark:text-txt-body">
                    Žiadne články neboli nájdené. Skúste inú tému alebo kratšie hľadanie.
                </p>
                <button v-if="hasFilters" type="button" class="mt-4 rounded-lg border px-4 py-2" @click="clearFilters">
                    Zobraziť všetky články
                </button>
            </div>
            <nav
                v-if="results && results.totalPages > 1"
                aria-label="Stránkovanie článkov"
                class="mt-10 flex items-center justify-center gap-4"
            >
                <NuxtLink
                    v-if="results.page > 1"
                    :to="pageLink(results.page - 1)"
                    rel="prev"
                    class="rounded-full border border-gray-300 px-4 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 dark:border-default"
                    >Predchádzajúca</NuxtLink
                >
                <span class="text-sm text-gray-600 dark:text-txt-body"
                    >Strana {{ results.page }} z {{ results.totalPages }}</span
                >
                <NuxtLink
                    v-if="results.page < results.totalPages"
                    :to="pageLink(results.page + 1)"
                    rel="next"
                    class="rounded-full border border-gray-300 px-4 py-2 focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary-600 dark:border-default"
                    >Ďalšia</NuxtLink
                >
            </nav>
        </template>
    </div>
</template>

<script setup lang="ts">
import type { BlogPost } from '#shared/types/Blog'
import { BLOG_TOPICS } from '#shared/utils/blog'
import { useApplicationStore } from '@/stores/application'

const store = useApplicationStore()
const route = useRoute()
const router = useRouter()
const isAuthenticated = computed(() => !!store.userProfile?.id)
const queryString = (value: unknown) => (typeof value === 'string' ? value : '')
const selectedTopic = computed(() => queryString(route.query.topic))
const selectedTag = computed(() => queryString(route.query.tag))
const committedQuery = computed(() => queryString(route.query.q).slice(0, 200))
const searchQuery = ref(committedQuery.value)
const hasFilters = computed(() => !!(selectedTopic.value || selectedTag.value || committedQuery.value))
const sectionTitle = computed(() =>
    committedQuery.value
        ? 'Výsledky hľadania'
        : BLOG_TOPICS.find((topic) => topic.id === selectedTopic.value)?.label || 'Najnovšie články'
)
const requestQuery = computed(() => ({
    paginated: 'true',
    summary: 'true',
    reactions: 'true',
    limit: 12,
    page: queryString(route.query.page) || '1',
    topic: selectedTopic.value,
    tag: selectedTag.value,
    q: committedQuery.value,
    ...(route.query.fresh ? { fresh: queryString(route.query.fresh) } : {})
}))
const dataKey = computed(() => `blog-discovery-${JSON.stringify(requestQuery.value)}`)
type BlogPage = { posts: BlogPost[]; total: number; page: number; totalPages: number }
const {
    data: results,
    status,
    error,
    refresh
} = await useAsyncData<BlogPage>(dataKey, () => $fetch('/api/blogs', { query: requestQuery.value }))
const { data: tags } = await useAsyncData('blog-discovery-tags', () => $fetch<string[]>('/api/blogs/tags'))
const allTags = computed(() => tags.value || [])

useHead({
    title: 'Blog GROWNi',
    meta: [{ name: 'description', content: 'Príbehy, tipy a inšpirácia pre váš osobný a profesionálny rast' }]
})

let searchTimeout: ReturnType<typeof setTimeout> | undefined
function cancelSearchTimer() {
    clearTimeout(searchTimeout)
}
function commitSearch() {
    cancelSearchTimer()
    const q = searchQuery.value.trim()
    if (q === committedQuery.value) return
    return router.replace({ query: { ...route.query, q: q || undefined, page: undefined } })
}
watch(searchQuery, () => {
    cancelSearchTimer()
    searchTimeout = setTimeout(commitSearch, 400)
})
watch(committedQuery, (value) => {
    cancelSearchTimer()
    searchQuery.value = value
})
onUnmounted(cancelSearchTimer)
function setFilter(key: 'topic' | 'tag', value: string) {
    cancelSearchTimer()
    return router.push({
        query: { ...route.query, q: searchQuery.value.trim() || undefined, [key]: value || undefined, page: undefined }
    })
}
function clearFilters() {
    cancelSearchTimer()
    searchQuery.value = ''
    return router.push({ query: { ...route.query, q: undefined, tag: undefined, topic: undefined, page: undefined } })
}
function pageLink(page: number) {
    return { path: '/blog', query: { ...route.query, page: page > 1 ? String(page) : undefined } }
}
</script>
